/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import TextButton from "@shared/v2/TextButton";
import Toggle from "@shared/v2/Toggle";
import Tooltip from "@shared/v2/Tooltip";
import { PencilSolidV6, TriangleExclamationSolidV6 } from "@shared/v2/Icomoon";

import NameWithAvatar from "./NameWithAvatar";
import RoutingNumbersDropdown from "./RoutingNumbersDropdown";
import {
  INBOUND_RECORDING_OPTIONS,
  INBOUND_RECORDING_OPTIONS_ADJUSTED,
  OUTBOUND_RECORDING_OPTIONS,
  OUTBOUND_RECORDING_OPTIONS_ADJUSTED,
} from "./helpers/recordingOptions";
import CallRecordingDropdown from "./CallRecordingDropdown";
import {
  updateRoutingNumber,
  updateUserRecordingSettings,
} from "../../reducers/layoutReducer/phoneSettingsReducer";
import {
  useAccountCallRecordingSettings,
  useCurrentUser,
  useFeatureFlags,
} from "../../reducers/layoutReducer/selectors";
import { OUTBOUND_INBOUND_RECORDING_NOTIFICATION_OPTIONS } from "./helpers/recordingNotificationOptions";

const CNAM_STATUS_HASH = {
  "in-review": "Pending",
  "pending-review": "Pending",
  "twilio-rejected": "Not Approved",
};

const STYLES = {
  cnamContainer: "tw-flex tw-w-[130px]",
};

const AgentRow = ({ addBrivityNumber, agent, openAddEditCnamModalHandlr }) => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const featureFlags = useFeatureFlags();
  const callRecordingAdjustmentsEnabled = featureFlags?.callRecordingAdjustments;
  const inboundRecordingOptions = callRecordingAdjustmentsEnabled
    ? INBOUND_RECORDING_OPTIONS_ADJUSTED
    : INBOUND_RECORDING_OPTIONS;
  const outboundRecordingOptions = callRecordingAdjustmentsEnabled
    ? OUTBOUND_RECORDING_OPTIONS_ADJUSTED
    : OUTBOUND_RECORDING_OPTIONS;
  const accountCallRecordingSettings = useAccountCallRecordingSettings();
  const inboundByNumberDisabled = accountCallRecordingSettings?.inboundRecording !== "by_number";
  const outboundByNumberDisabled = accountCallRecordingSettings?.outboundRecording !== "by_number";
  const inboundDisabled = inboundByNumberDisabled
    ? accountCallRecordingSettings?.inboundRecording === "disabled"
    : agent.callRecordingSettings?.inboundRecording === "disabled" ||
      !agent.callRecordingSettings?.inboundRecording;
  const outboundDisabled = outboundByNumberDisabled
    ? accountCallRecordingSettings?.outboundRecording === "disabled"
    : agent.callRecordingSettings?.outboundRecording === "disabled" ||
      !agent.callRecordingSettings?.outboundRecording;
  const managedNumber = agent.managedNumber ?? (
    <div className="tw-flex tw-w-[85px]">
      <TextButton onClick={() => addBrivityNumber(agent)} className="!tw-pl-[0px]">
        Add
      </TextButton>
    </div>
  );

  const cnamEditable = currentUser.account.isAdmin || currentUser.id === agent.id;

  return (
    <tr>
      <td className="tw-px-[8px] tw-py-[16px]">
        <NameWithAvatar
          aria-label="agent"
          name={agent.fullName}
          role={agent.role}
          avatarUrl={agent.avatarUrl}
          showPath={agent.showPath}
        />
      </td>
      <td className="tw-px-[8px] tw-py-[16px]">
        <RoutingNumbersDropdown
          aria-label="routing numbers"
          numbers={agent.routingNumbers}
          selectedNumberId={agent.routingNumberId}
          isLoading={agent.loading}
          onChange={(o) => dispatch(updateRoutingNumber({ ...o.meta, userId: agent.id }))}
        />
      </td>
      <td className="tw-px-[8px] tw-py-[16px]">{managedNumber}</td>
      {featureFlags.cnamRegistration && (
        <td className="tw-px-[8px] tw-py-[16px]">
          {(() => {
            if (!cnamEditable) {
              return <span className={`tw-text-gray-50 ${STYLES.cnamContainer}`}>—</span>;
            }

            if (agent.twilioCnam) {
              const pendingCnam =
                agent.twilioCnam?.status === "pending-review" || agent.twilioCnam?.status === "in-review";

              return (
                <div className={STYLES.cnamContainer}>
                  <div
                    className="tw-flex tw-flex-col tw-cursor-pointer"
                    onClick={() =>
                      openAddEditCnamModalHandlr({
                        id: agent.twilioCnam.id,
                        userId: agent.id,
                        displayName: agent.twilioCnam.displayName,
                        action: "edit",
                      })
                    }
                  >
                    <span className="tw-text-14d">
                      {agent.twilioCnam.displayName}{" "}
                      <PencilSolidV6 className="tw-text-theme-text-button-color" />
                    </span>
                    {agent.twilioCnam?.status !== "twilio-approved" && (
                      <Tooltip
                        innerClassName={`tw-max-w-[216px] !tw-text-left ${pendingCnam ? "tw-hidden" : ""}`}
                        arrowClassName={pendingCnam ? "tw-hidden" : ""}
                        placement="right"
                        trigger={
                          <span
                            className={`tw-capitalize tw-flex tw-items-center tw-gap-4px ${
                              pendingCnam ? "tw-text-yellow-100" : "tw-text-[#FA423C]"
                            }`}
                          >
                            {CNAM_STATUS_HASH[agent.twilioCnam?.status]}{" "}
                            <TriangleExclamationSolidV6 size="s" />
                          </span>
                        }
                        content="Unfortunately, your Caller ID Name was rejected. You can submit another Caller ID Name by clicking the edit icon."
                      />
                    )}
                  </div>
                </div>
              );
            }

            return (
              <div className={STYLES.cnamContainer}>
                <TextButton
                  className="!tw-pl-[0px]"
                  disabled={!agent.managedNumber}
                  onClick={() => {
                    openAddEditCnamModalHandlr({
                      userId: agent.id,
                      displayName: agent.fullName,
                      action: "add",
                    });
                  }}
                >
                  Add
                </TextButton>
              </div>
            );
          })()}
        </td>
      )}
      {featureFlags.callRecording && (
        <>
          <td className="tw-px-[8px] tw-py-[16px] tw-align-top">
            <CallRecordingDropdown
              aria-label="Inbound settings"
              options={inboundRecordingOptions}
              isDisabled={!currentUser.account.isAdmin || inboundByNumberDisabled}
              value={inboundRecordingOptions.find(
                (o) =>
                  o.value ===
                  (inboundByNumberDisabled
                    ? accountCallRecordingSettings?.inboundRecording
                    : agent.callRecordingSettings?.inboundRecording || "disabled"),
              )}
              loading={agent.loading}
              onChange={(option) => {
                dispatch(
                  updateUserRecordingSettings({
                    userUuid: agent.uuid,
                    settings: {
                      inboundRecording: option.value,
                    },
                  }),
                );
              }}
            />
            {!inboundDisabled && (
              <div className="tw-ml-[2px] tw-text-12d tw-text-neutral-gray-30">
                Notification Message:{" "}
                {
                  OUTBOUND_INBOUND_RECORDING_NOTIFICATION_OPTIONS.find(
                    (o) => o.value === accountCallRecordingSettings?.inboundNotificationMessageCondition,
                  )?.label
                }
              </div>
            )}
          </td>
          <td className="tw-px-[8px] tw-py-[16px]">
            <CallRecordingDropdown
              aria-label="Outbound settings"
              options={outboundRecordingOptions}
              isDisabled={!currentUser.account.isAdmin || outboundByNumberDisabled}
              value={outboundRecordingOptions.find(
                (o) =>
                  o.value ===
                  (outboundByNumberDisabled
                    ? accountCallRecordingSettings?.outboundRecording
                    : agent.callRecordingSettings?.outboundRecording || "disabled"),
              )}
              loading={agent.loading}
              onChange={(option) => {
                dispatch(
                  updateUserRecordingSettings({
                    userUuid: agent.uuid,
                    settings: {
                      outboundRecording: option.value,
                    },
                  }),
                );
              }}
            />
            {!outboundDisabled && (
              <div className="tw-ml-[2px] tw-text-12d tw-text-neutral-gray-30">
                Notification Message:{" "}
                {
                  OUTBOUND_INBOUND_RECORDING_NOTIFICATION_OPTIONS.find(
                    (o) => o.value === accountCallRecordingSettings?.outboundNotificationMessageCondition,
                  )?.label
                }
              </div>
            )}
          </td>
          {callRecordingAdjustmentsEnabled && (
            <td className="tw-px-[8px] tw-py-[16px]" align="center">
              <Toggle
                labelClassName="tw-w-[40px]"
                checked={
                  inboundDisabled && outboundDisabled
                    ? false
                    : (agent.callRecordingSettings?.aiCallSummary ?? true)
                }
                disabled={agent.loading || (inboundDisabled && outboundDisabled)}
                onClick={() => {
                  dispatch(
                    updateUserRecordingSettings({
                      userUuid: agent.uuid,
                      settings: {
                        aiCallSummary: !(agent.callRecordingSettings?.aiCallSummary ?? true),
                      },
                    }),
                  );
                }}
              />
            </td>
          )}
        </>
      )}
    </tr>
  );
};

AgentRow.propTypes = {
  addBrivityNumber: PropTypes.func.isRequired,
  openAddEditCnamModalHandlr: PropTypes.func.isRequired,
  agent: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    uuid: PropTypes.string,
    loading: PropTypes.bool,
    managedNumber: PropTypes.string,
    fullName: PropTypes.string,
    role: PropTypes.string,
    avatarUrl: PropTypes.string,
    showPath: PropTypes.string,
    routingNumbers: PropTypes.arrayOf(PropTypes.shape({})),
    routingNumberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    callRecordingSettings: PropTypes.shape(),
    twilioCnam: PropTypes.shape({}),
  }).isRequired,
};

export default AgentRow;
